import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useLanguageNavigate } from "../../hooks/useLanguageNavigation";

import CopyLinkWithoutBackground from "../iFrame/copyLinkWithoutBackground";
import {
  getProductItemById,
  deleteProductItem,
  createProductItem,
} from "../../redux/reducer/productReducer";
import { getCurrencyAndLanguageSymbol } from "../../constent/currencyUtils";
import { getAddressesByUserId } from "../../redux/reducer/userAddressReducer";

export default function StickyCart() {
  const currencySymbol = getCurrencyAndLanguageSymbol();
  // const productsFromReduxCart = useSelector((state) => state.shopCart.cart || []);
  const { addresses } = useSelector((state) => state.userAddress);
  const accessToken = localStorage.getItem("accessToken");

  const navigateWithLang = useLanguageNavigate();
  const dispatch = useDispatch();
  const cartRef = useRef();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const isInDashboard = location.pathname.includes("/my-account/buy-for-me");

  const isRTL = i18n.language === "ar";
  const currentUser = useSelector((state) => state.auth?.user);
  const token = localStorage.getItem("accessToken");
  const isLoggedIn = !!currentUser || !!token;

  const productsFromRedux = useSelector(
    (state) => state.products?.allProducts || []
  );
  const productsFromLocalStorage =
    JSON.parse(localStorage.getItem("cartData")) || [];

  const [open, setOpen] = useState(null);
  const [userClosed, setUserClosed] = useState(false);

  useEffect(() => {
    const fetchAddresses = async () => {
      if (accessToken) {
        await dispatch(getAddressesByUserId());
      }
    };
    fetchAddresses();
  }, [dispatch, accessToken]);
  // console.log(addresses?.user?.role);

  const handleLoginClick = () => {
    setOpen(false);
    navigateWithLang("/login");
  };
  const handleSignClick = () => {
    setOpen(false);
    navigateWithLang("/registration");
  };
  const handledash = () => {
    setOpen(false);
    setTimeout(() => {
      navigateWithLang("/my-account/buy-for-me");
    }, 100);
  };

  useEffect(() => {
    if (isLoggedIn) {
      dispatch(getProductItemById());
      const localCart = JSON.parse(localStorage.getItem("cartData")) || [];
      if (localCart.length > 0) {
        sendCartToAPI(localCart);
        localStorage.removeItem("cartData");
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem("cartData")) || [];
      if (localCart.length > 0) {
        sendCartToAPI(localCart);
      }
    }
  }, [isLoggedIn, dispatch]);

  const mergedProducts = [
    ...productsFromLocalStorage.map((item) => ({
      name: item.name,
      link: item.link || "",
      size: item.size || "",
      count: item.count || 1,
      additionalInfo: item.additionalInfo || "",
      img:
        item.img ||
        item.image ||
        "https://api.markaship.com/uploads/waiting.png",
      status: (item.status || "").toLowerCase(),
      id: item.id,
      price: item.price || 0,
    })),
    ...productsFromRedux.map((item) => ({
      name: item?.name,
      link: item?.link || "",
      size: item?.size || "",
      count: item?.count || 1,
      additionalInfo: item?.additionalInfo || "",
      img:
        item?.img ||
        item?.image ||
        "https://api.markaship.com/uploads/waiting.png",
      status: (item?.status || "").toLowerCase(),
      id: item?._id,
      price: item?.price || 0,
    })),
  ];
  const hasPendingOrPricing = mergedProducts.filter(
    (item) => item.status === "pending" || item.status === "priced"
  );
  const prevCountRef = useRef(hasPendingOrPricing.length);
  const skipNextOpen = useRef(false);

  useEffect(() => {
    if (isLoggedIn && !skipNextOpen.current) {
      skipNextOpen.current = true;
      prevCountRef.current = hasPendingOrPricing.length;
      return;
    }
    // else if (
    //   !isInDashboard &&
    //   !userClosed &&
    //   mergedProducts.length > prevCountRef.current
    // ) {
    //   // setOpen(false);
    // }
    else if (!isLoggedIn && hasPendingOrPricing.length > prevCountRef.current) {
      setOpen(true);
    }

    prevCountRef.current = hasPendingOrPricing.length;
  }, [hasPendingOrPricing, userClosed, isInDashboard, isLoggedIn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (cartRef.current && !cartRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // for open sticky cart from other components
  useEffect(() => {
    const handleOpenCart = () => {
      setOpen(true);
    };

    window.addEventListener("openCart", handleOpenCart);

    return () => {
      window.removeEventListener("openCart", handleOpenCart);
    };
  }, []);

  const handleRemoveProduct = async (item) => {
    if (item.id.startsWith("local-")) {
      // console.log("Removing from local storage:", item);
      removeFromLocalCart(item);
    } else if (isLoggedIn) {
      try {
        await dispatch(deleteProductItem(item.id)).unwrap();
        dispatch(getProductItemById());
        dispatch({ type: "shopCart/removeCartItem", payload: item.id });
      } catch (error) {
        console.error("Failed to delete item:", error);
        alert(error?.message || t("FailedDeleteItem") || "فشل في حذف العنصر");
      }
    } else {
      alert("Please login to remove this product.");
    }

    setOpen(false);
    setTimeout(() => setOpen(true), 100);
  };

  const removeFromLocalCart = (item) => {
    const existing = JSON.parse(localStorage.getItem("cartData")) || [];
    // console.log(item);
    const updated = existing.filter(
      (p) =>
        !(
          p.id === item.id &&
          p.name === item.name &&
          p.size === item.size &&
          p.link === item.link &&
          p.additionalInfo === item.additionalInfo
        )
    );
    localStorage.setItem("cartData", JSON.stringify(updated));
    dispatch({ type: "shopCart/setCart", payload: updated });
  };

  const sendCartToAPI = async (products) => {
    try {
      if (!token) {
        // alert(t("cart.LoginRequired"));
        return;
      }

      await Promise.all(
        products.map((product) => {
          const payload = {
            name: product.name,
            link: product.link || "",
            size: product.size || "",
            count: product.count || 1,
            additionalInfo: product.additionalInfo || "",
            img: product.img || "https://api.markaship.com/uploads/waiting.png",
          };
          dispatch(createProductItem(payload)).unwrap();
          // return axios.post(`${ApiBase}/product/builder`, payload, {
          //   headers: { token, "Content-Type": "application/json" },
          // });
        })
      );

      dispatch(getProductItemById());
    } catch (error) {
      console.error("Error sending cart to API:", error);
    } finally {
    }
  };

  const pricedProducts = mergedProducts.filter(
    (item) => item.status === "priced"
  );
  const unpricedProducts = mergedProducts.filter(
    (item) => item.status === "pending"
  );

  if (addresses?.user?.role === "admin") {
    return null;
  }
  // console.log(productsFromRedux);
  return (
    <>
      {/*Button Cart*/}
      <div className="fixed right-0 z-50 top-40">
        <button
          onClick={() => {
            dispatch(getProductItemById());
            setOpen(true);
          }}
          className="flex items-center justify-center w-12 h-12 text-2xl font-bold text-white rounded-lg rounded-r-none shadow-lg bg-custom-yellow"
        >
          <ShoppingCartIcon className="w-6 h-6 text-black" />
        </button>

        {hasPendingOrPricing?.length > 0 && (
          <span className="absolute flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-600 rounded-full -top-1 -left-1">
            {hasPendingOrPricing?.length}
          </span>
        )}
      </div>

      {/*background overlay*/}
      <div
        className={`fixed inset-0 z-40 bg-black bg-opacity-20 transition-opacity duration-300 ${
          open
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => {
          setOpen(false);
          setUserClosed(true);
        }}
      ></div>

      {/* Cart Content */}
      <div
        ref={cartRef}
        className={`fixed top-0 right-0 z-50 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out ${
          open ? "translate-x-0" : "translate-x-full"
        } flex flex-col`}
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="relative flex items-center justify-center p-4 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">
            {isLoggedIn ? t("cart.LatestPurchases") : t("cart.YourCart")}
          </h3>
          <button
            onClick={() => {
              setOpen(false);
              setUserClosed(true);
            }}
            className={`absolute -top-2 text-4xl font-bold text-gray-500 hover:text-red-600 w-16 h-16 flex items-center justify-center rounded-full ${
              isRTL ? "left-2" : "right-2"
            }`}
            title={t("Close")}
          >
            ×
          </button>
        </div>

        <div className="flex flex-col flex-1 gap-4 p-4 overflow-y-auto">
          <CopyLinkWithoutBackground />

          {pricedProducts.length > 0 && (
            <div className="p-2 border-4 border-green-400 rounded-md">
              <h4 className="mb-2 text-sm font-bold text-green-400">
                ✅ {t("Priced Products")}
              </h4>
              {pricedProducts.map((item, idx) => (
                <div
                  key={idx}
                  className="relative flex items-start gap-2 p-2 border-b border-gray-100 rounded hover:bg-gray-50"
                >
                  <button
                    onClick={() => handleRemoveProduct(item)}
                    className={`absolute top-2 flex items-center justify-center w-10 h-10 rounded-full text-3xl font-bold text-gray-500 hover:text-red-600 ${
                      isRTL ? "left-2" : "right-2"
                    }`}
                  >
                    ×
                  </button>
                  <img
                    src={item.img}
                    alt={item.name}
                    className="object-cover w-16 h-16 rounded"
                  />
                  <div className="flex flex-col text-xs text-gray-700">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-600 hover:underline"
                    >
                      {item.name}
                    </a>
                    <span>
                      {t("Count")}: {item.count}
                    </span>
                    {item.size && (
                      <span>
                        {t("Size")}: {item.size}
                      </span>
                    )}
                    {item.additionalInfo && (
                      <span>
                        {t("Note")}: {item.additionalInfo}
                      </span>
                    )}
                    {item.price && (
                      <span>
                        {t("Price")}: {(item.price * item.count).toFixed(2)}{" "}
                        {currencySymbol}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {unpricedProducts.length > 0 && (
            <div className="p-2 border-4 border-yellow-400 rounded-md">
              <h4 className="mb-2 text-sm font-bold text-yellow-400">
                {t("ProductsNotYetPriced")}
              </h4>
              {unpricedProducts.map((item, idx) => (
                <div
                  key={idx}
                  className="relative flex items-start gap-2 p-2 border-b border-gray-100 rounded hover:bg-gray-50"
                >
                  <button
                    onClick={() => handleRemoveProduct(item)}
                    className={`absolute top-2 flex items-center justify-center w-10 h-10 rounded-full text-3xl font-bold text-gray-500 hover:text-red-600 ${
                      isRTL ? "left-2" : "right-2"
                    }`}
                  >
                    ×
                  </button>
                  <img
                    src={item.img}
                    alt={item.name}
                    className="object-cover w-16 h-16 rounded"
                  />
                  <div className="flex flex-col text-xs text-gray-700">
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-blue-600 hover:underline"
                    >
                      {item.name}
                    </a>
                    <span>
                      {t("Count")}: {item.count}
                    </span>
                    {item.size && (
                      <span>
                        {t("Size")}: {item.size}
                      </span>
                    )}
                    {item.additionalInfo && (
                      <span>
                        {t("Note")}: {item.additionalInfo}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {isLoggedIn ? (
          <button
            onClick={handledash}
            className="py-2 mx-4 my-2 text-sm font-medium text-black shadow bg-custom-yellow hover:opacity-95"
          >
            {t("Go to Dashboard")}
          </button>
        ) : (
          <>
            <button
              onClick={handleLoginClick}
              className="py-2 mx-4 my-2 text-sm font-medium text-black shadow bg-custom-yellow hover:opacity-95"
            >
              {t("cart.Login")}
            </button>
            <button
              onClick={handleSignClick}
              className="py-2 mx-4 my-2 text-sm font-medium text-black shadow bg-custom-yellow hover:opacity-95"
            >
              {t("cart.signup")}
            </button>
          </>
        )}
      </div>
    </>
  );
}
